import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Card,
    Typography,
    TextField,
    Button,
    Grid,
    Alert,
    Avatar,
    IconButton,
    Stack,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Chip,
    useTheme,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Slider
} from '@mui/material';
import Cropper from 'react-easy-crop';
import {
    Person,
    Lock,
    Edit,
    Save,
    Cancel,
    Visibility,
    VisibilityOff,
    Phone,
    CheckCircle,
    Home,
    LocalShipping,
    Send,
    PhotoCamera,
    Delete
} from '@mui/icons-material';
import { ClientGetMyAccountInfo, ClientUpdateProfile, ClientUpdatePassword, ClientGetRouteList, ClientUpdateWhatsAppNumber, ClientRequestWhatsAppOTP, ClientCheckWhatsappExists, ClientUploadProfilePicture, ClientDeleteProfilePicture } from 'api/client';
import { getMyAccount } from 'api/wallet';
import DeliveryAddressList from 'components/DeliveryAddressList';
import ForwardAddressList from 'components/ForwardAddressList';
import OTPInput from 'components/OTPInput';
import uuid from 'react-uuid';
import { generateUniqueId } from 'utils';

const MyProfile = () => {
    const theme = useTheme();
    const autoSaveTimeoutRef = useRef(null);

    // Helper function to normalize address data structure
    const normalizeAddresses = (addresses) => {
        if (!Array.isArray(addresses)) return [];
        return addresses.map((addr, index) => {
            if (typeof addr === 'string') {
                return {
                    id: uuid(),
                    map: addr,
                    title: `Address ${index + 1}`,
                    description: addr,
                    isDefault: index === 0,
                    lat: null, lng: null,
                    contactName: '', contactPhone: '',
                    floor: '', unitNumber: '', notes: '',
                    photos: [],
                };
            }
            return {
                id: addr.id || uuid(),
                map: addr.map || addr.address || '',
                title: addr.title || `Address ${index + 1}`,
                description: addr.description || addr.address || addr.map || '',
                isDefault: addr.isDefault !== undefined ? addr.isDefault : index === 0,
                lat: addr.lat ?? null,
                lng: addr.lng ?? null,
                contactName: addr.contactName || '',
                contactPhone: addr.contactPhone || '',
                floor: addr.floor || '',
                unitNumber: addr.unitNumber || '',
                notes: addr.notes || '',
                photos: addr.photos || [],
            };
        });
    };

    // Helper function to convert delivery addresses to backend format
    const convertDeliveryAddressesToBackend = (addresses) => {
        if (!Array.isArray(addresses)) return [];
        return addresses.map(addr => ({
            id: addr.id,
            map: addr.map || '',
            title: addr.title || '',
            description: addr.description || '',
            isDefault: addr.isDefault,
            lat: addr.lat ?? null,
            lng: addr.lng ?? null,
            contactName: addr.contactName || '',
            contactPhone: addr.contactPhone || '',
            floor: addr.floor || '',
            unitNumber: addr.unitNumber || '',
            notes: addr.notes || '',
            photos: addr.photos || [],
        }));
    };

    // Helper function to convert forward addresses to backend format
    const convertForwardAddressesToBackend = (addresses) => {
        if (!Array.isArray(addresses)) return [];
        return addresses.map(addr => ({
            id: addr.id,
            logisticCompany: addr.logisticCompany || '',
            branch: addr.branch || '',
            description: addr.description || '',
            isDefault: addr.isDefault
        }));
    };

    // Helper function to normalize forward address data structure
    const normalizeForwardAddresses = (addresses) => {
        if (!Array.isArray(addresses)) return [];
        return addresses.map((addr, index) => {
            // If address is just a string (old format)
            if (typeof addr === 'string') {
                return {
                    id: uuid(),
                    logisticCompany: '',
                    branch: '',
                    description: addr,
                    isDefault: index === 0
                };
            }
            // If address is an object but missing required fields
            return {
                id: addr.id || uuid(),
                logisticCompany: addr.logisticCompany || '',
                branch: addr.branch || '',
                description: addr.description || '',
                isDefault: addr.isDefault !== undefined ? addr.isDefault : index === 0
            };
        });
    };

    // Auto-save function for addresses with debouncing
    const autoSaveAddresses = (updatedProfileForm) => {
        if (!userAccount && !customer) {
            console.warn('No user data available for auto-save');
            return;
        }

        // Clear existing timeout
        if (autoSaveTimeoutRef.current) {
            clearTimeout(autoSaveTimeoutRef.current);
        }

        // Set new timeout for debouncing
        autoSaveTimeoutRef.current = setTimeout(async () => {
            try {
                const response = await ClientUpdateProfile(
                    updatedProfileForm.name?.trim() || profileForm.name?.trim() || '',
                    updatedProfileForm.default_route || profileForm.default_route || '',
                    updatedProfileForm.isDeliveryRequired,
                    convertDeliveryAddressesToBackend(updatedProfileForm.deliveryAddresses),
                    updatedProfileForm.isForwardRequired,
                    convertForwardAddressesToBackend(updatedProfileForm.forwardAddresses)
                );

                if (response && response.success) {
                    console.log('Addresses auto-saved successfully');
                    // Optionally show a brief, non-intrusive success indicator
                    showAlert('profile', 'success', 'Address saved!');
                    setTimeout(() => {
                        setAlerts(prev => ({
                            ...prev,
                            profile: { show: false, type: '', message: '' }
                        }));
                    }, 2000); // Auto-hide after 2 seconds
                } else {
                    console.error('Auto-save failed:', response?.message);
                    showAlert('profile', 'warning', 'Failed to auto-save address changes');
                }
            } catch (error) {
                console.error('Error during auto-save:', error);
                showAlert('profile', 'warning', 'Failed to auto-save address changes');
            }
        }, 1000); // 1 second debounce
    };

    const [customer, setCustomer] = useState(null);
    const [userAccount, setUserAccount] = useState(null);
    const [routes, setRoutes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [updateLoading, setUpdateLoading] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [showPasswordDialog, setShowPasswordDialog] = useState(false);

    // Profile form state
    const [profileForm, setProfileForm] = useState({
        name: '',
        whatsapp: '',
        default_route: '',
        isDeliveryRequired: true,
        deliveryAddresses: [],
        isForwardRequired: true,
        forwardAddresses: []
    });

    // Password form state
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });

    const [alerts, setAlerts] = useState({
        profile: { show: false, type: '', message: '' },
        password: { show: false, type: '', message: '' },
        whatsapp: { show: false, type: '', message: '' }
    });

    // WhatsApp update dialog state
    const [showWhatsAppDialog, setShowWhatsAppDialog] = useState(false);
    const [whatsappUpdateStep, setWhatsappUpdateStep] = useState(1); // 1: password, 2: new number, 3: OTP
    const [whatsappUpdateForm, setWhatsappUpdateForm] = useState({
        currentPassword: '',
        newWhatsApp: '',
        otp: new Array(6).fill(''),
        ref: ''
    });
    const [whatsappUpdateLoading, setWhatsappUpdateLoading] = useState(false);

    // Profile picture state
    const [profilePictureFile, setProfilePictureFile] = useState(null);
    const [profilePicturePreview, setProfilePicturePreview] = useState(null);
    const [profilePictureUploading, setProfilePictureUploading] = useState(false);
    const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
    const fileInputRef = useRef(null);

    // Crop dialog state
    const [cropDialogOpen, setCropDialogOpen] = useState(false);
    const [rawImageSrc, setRawImageSrc] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [cropApplying, setCropApplying] = useState(false);

    const loadUserData = useCallback(async () => {
        setLoading(true);
        try {
            // Load routes first
            const routesResponse = await ClientGetRouteList();
            if (routesResponse && routesResponse.success) {
                setRoutes(routesResponse.data || []);
            }

            // Load customer data first
            const customerResponse = await ClientGetMyAccountInfo();
            console.log('Customer Response:', customerResponse);

            let customerData = null;
            if (customerResponse && customerResponse.status === 200 && customerResponse.customer) {
                customerData = customerResponse.customer;
                setCustomer(customerData);
            }

            // Load user account data for email and other user details
            const accountResponse = await getMyAccount();
            console.log('Account Response:', accountResponse);

            if (accountResponse && accountResponse.success && accountResponse.user) {
                setUserAccount(accountResponse.user);
                // Populate form with user account data, fallback to customer data
                setProfileForm({
                    name: accountResponse.user.name || customerData?.name || '',
                    whatsapp: accountResponse.user.whatsapp || customerData?.whatsapp || '',
                    default_route: customerData?.default_route || '',
                    isDeliveryRequired: customerData?.isDeliveryRequired || false,
                    deliveryAddresses: normalizeAddresses(customerData?.deliveryAddresses || []),
                    isForwardRequired: customerData?.isForwardRequired || false,
                    forwardAddresses: normalizeForwardAddresses(customerData?.forwardAddresses || [])
                });
            } else if (customerData) {
                // Fallback to customer data if user account is not available
                setProfileForm({
                    name: customerData.name || '',
                    whatsapp: customerData.whatsapp || '',
                    default_route: customerData.default_route || '',
                    isDeliveryRequired: customerData.isDeliveryRequired || false,
                    deliveryAddresses: normalizeAddresses(customerData.deliveryAddresses || []),
                    isForwardRequired: customerData.isForwardRequired || false,
                    forwardAddresses: normalizeForwardAddresses(customerData.forwardAddresses || [])
                });
            } else {
                // Set empty form if no data available
                setProfileForm({
                    name: '',
                    whatsapp: '',
                    default_route: '',
                    isDeliveryRequired: false,
                    deliveryAddresses: [],
                    isForwardRequired: false,
                    forwardAddresses: []
                });
            }
        } catch (error) {
            console.error('Error loading user data:', error);
            showAlert('profile', 'error', 'Failed to load user data');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadUserData();
    }, [loadUserData]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (autoSaveTimeoutRef.current) {
                clearTimeout(autoSaveTimeoutRef.current);
            }
        };
    }, []);

    const showAlert = (type, severity, message) => {
        setAlerts(prev => ({
            ...prev,
            [type]: { show: true, type: severity, message }
        }));

        // Auto hide after 5 seconds
        setTimeout(() => {
            setAlerts(prev => ({
                ...prev,
                [type]: { show: false, type: '', message: '' }
            }));
        }, 5000);
    };

    const openPasswordDialog = () => {
        setShowPasswordDialog(true);
        // Clear password form
        setPasswordForm({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
        });
        // Clear password alerts
        setAlerts(prev => ({
            ...prev,
            password: { show: false, type: '', message: '' }
        }));

        // Additional clear after dialog opens to prevent browser autocomplete
        setTimeout(() => {
            setPasswordForm({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
        }, 100);
    };

    const closePasswordDialog = () => {
        setShowPasswordDialog(false);
        // Clear password form
        setPasswordForm({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
        });
        // Clear password alerts
        setAlerts(prev => ({
            ...prev,
            password: { show: false, type: '', message: '' }
        }));
    };

    const handleProfileInputChange = (field, value) => {
        setProfileForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const setDeliveryAddresses = (addresses) => {
        setProfileForm(prev => {
            const newAddresses = typeof addresses === 'function' ? addresses(prev.deliveryAddresses) : addresses;
            const updatedForm = {
                ...prev,
                deliveryAddresses: newAddresses
            };

            // Trigger auto-save
            autoSaveAddresses(updatedForm);

            return updatedForm;
        });
    };

    const setForwardAddresses = (addresses) => {
        setProfileForm(prev => {
            const newAddresses = typeof addresses === 'function' ? addresses(prev.forwardAddresses) : addresses;
            const updatedForm = {
                ...prev,
                forwardAddresses: newAddresses
            };

            // Trigger auto-save
            autoSaveAddresses(updatedForm);

            return updatedForm;
        });
    };

    // Crop utility — draws the cropped region onto a canvas and returns a Blob
    const getCroppedImg = (imageSrc, pixelCrop) =>
        new Promise((resolve, reject) => {
            const image = new Image();
            image.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = pixelCrop.width;
                canvas.height = pixelCrop.height;
                canvas.getContext('2d').drawImage(
                    image,
                    pixelCrop.x, pixelCrop.y,
                    pixelCrop.width, pixelCrop.height,
                    0, 0,
                    pixelCrop.width, pixelCrop.height
                );
                canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92);
            };
            image.onerror = reject;
            image.src = imageSrc;
        });

    // Profile picture functions
    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (!file) return;
        if (fileInputRef.current) fileInputRef.current.value = '';

        if (!file.type.startsWith('image/')) {
            showAlert('profile', 'error', 'Please select a valid image file');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            showAlert('profile', 'error', 'Image file must be less than 5MB');
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            setRawImageSrc(e.target.result);
            setCrop({ x: 0, y: 0 });
            setZoom(1);
            setCropDialogOpen(true);
        };
        reader.readAsDataURL(file);
    };

    const handleApplyCrop = async () => {
        if (!croppedAreaPixels) return;
        setCropApplying(true);
        try {
            const blob = await getCroppedImg(rawImageSrc, croppedAreaPixels);
            const croppedFile = new File([blob], 'profile.jpg', { type: 'image/jpeg' });
            setProfilePictureFile(croppedFile);
            const reader = new FileReader();
            reader.onload = (e) => setProfilePicturePreview(e.target.result);
            reader.readAsDataURL(blob);
            setCropDialogOpen(false);
            setRawImageSrc(null);
        } catch (e) {
            showAlert('profile', 'error', 'Failed to crop image');
        } finally {
            setCropApplying(false);
        }
    };

    const handleCancelCrop = () => {
        setCropDialogOpen(false);
        setRawImageSrc(null);
    };

    const syncCustomerLocalStorage = (patch) => {
        try {
            const stored = JSON.parse(localStorage.getItem('customer') || '{}');
            const updated = { ...stored, ...patch, _updatedAt: Date.now() };
            localStorage.setItem('customer', JSON.stringify(updated));
            window.dispatchEvent(new Event('customer-updated'));
        } catch {}
    };

    const handleProfilePictureUpload = async () => {
        if (!profilePictureFile) return;

        setProfilePictureUploading(true);
        try {
            const response = await ClientUploadProfilePicture(profilePictureFile);

            if (response && response.success) {
                showAlert('profile', 'success', 'Profile picture updated successfully!');
                if (response.data?.filename) {
                    syncCustomerLocalStorage({ profilePicture: response.data.filename });
                }
                await loadUserData();
                setProfilePictureFile(null);
                setProfilePicturePreview(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
            } else {
                showAlert('profile', 'error', response?.message || 'Failed to upload profile picture');
            }
        } catch (error) {
            console.error('Error uploading profile picture:', error);
            showAlert('profile', 'error', 'Failed to upload profile picture');
        } finally {
            setProfilePictureUploading(false);
        }
    };

    const handleProfilePictureDelete = async () => {
        setProfilePictureUploading(true);
        try {
            const response = await ClientDeleteProfilePicture();

            if (response && response.success) {
                showAlert('profile', 'success', 'Profile picture removed successfully!');
                syncCustomerLocalStorage({ profilePicture: undefined });
                await loadUserData();
            } else {
                showAlert('profile', 'error', response?.message || 'Failed to remove profile picture');
            }
        } catch (error) {
            console.error('Error deleting profile picture:', error);
            showAlert('profile', 'error', 'Failed to remove profile picture');
        } finally {
            setProfilePictureUploading(false);
            setShowDeleteConfirmDialog(false);
        }
    };

    const confirmDeleteProfilePicture = () => {
        setShowDeleteConfirmDialog(true);
    };

    const cancelProfilePictureUpload = () => {
        setProfilePictureFile(null);
        setProfilePicturePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handlePasswordInputChange = (field, value) => {
        setPasswordForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const togglePasswordVisibility = (field) => {
        setShowPasswords(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const handleUpdateProfile = async () => {
        if (!userAccount && !customer) {
            showAlert('profile', 'error', 'No user data available for update');
            return;
        }

        // Validate required fields
        if (!profileForm.name || !profileForm.name.trim()) {
            showAlert('profile', 'error', 'Name is required');
            return;
        }

        setUpdateLoading(true);
        try {
            const response = await ClientUpdateProfile(
                profileForm.name.trim(),
                profileForm.default_route || '',
                profileForm.isDeliveryRequired,
                convertDeliveryAddressesToBackend(profileForm.deliveryAddresses),
                profileForm.isForwardRequired,
                convertForwardAddressesToBackend(profileForm.forwardAddresses)
            );

            if (response && response.success) {
                showAlert('profile', 'success', 'Profile updated successfully!');
                setIsEditing(false);
                setShowSuccessDialog(true);
                // Reload data to get updated information
                loadUserData();
            } else {
                showAlert('profile', 'error', response?.message || 'Failed to update profile');
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            showAlert('profile', 'error', 'An error occurred while updating your profile');
        } finally {
            setUpdateLoading(false);
        }
    };

    const handleChangePassword = async () => {
        if (!userAccount && !customer) {
            showAlert('password', 'error', 'No user data available for password change');
            return;
        }

        // Validate password form
        if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
            showAlert('password', 'error', 'All password fields are required');
            return;
        }

        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            showAlert('password', 'error', 'New passwords do not match');
            return;
        }

        if (passwordForm.newPassword.length < 8) {
            showAlert('password', 'error', 'New password must be at least 8 characters long and include both letters and numbers');
            return;
        }

        // Validate password strength (letters and numbers)
        const hasLetters = /[a-zA-Z]/.test(passwordForm.newPassword);
        const hasNumbers = /\d/.test(passwordForm.newPassword);
        if (!hasLetters || !hasNumbers) {
            showAlert('password', 'error', 'Password must include both letters and numbers');
            return;
        }

        setUpdateLoading(true);
        try {
            const response = await ClientUpdatePassword(
                passwordForm.currentPassword,
                passwordForm.newPassword
            );

            if (response && response.status === 200) {
                showAlert('password', 'success', 'Password changed successfully!');
                setPasswordForm({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                });
                closePasswordDialog();
                setShowSuccessDialog(true);
            } else {
                showAlert('password', 'error', response?.message || 'Failed to change password');
            }
        } catch (error) {
            console.error('Error changing password:', error);
            showAlert('password', 'error', 'An error occurred while changing your password');
        } finally {
            setUpdateLoading(false);
        }
    };

    // WhatsApp update dialog functions
    const openWhatsAppDialog = () => {
        setShowWhatsAppDialog(true);
        setWhatsappUpdateStep(1);
        setWhatsappUpdateForm({
            currentPassword: '',
            newWhatsApp: '',
            otp: new Array(6).fill(''),
            ref: ''
        });
        setAlerts(prev => ({
            ...prev,
            whatsapp: { show: false, type: '', message: '' }
        }));
    };

    const closeWhatsAppDialog = () => {
        setShowWhatsAppDialog(false);
        setWhatsappUpdateStep(1);
        setWhatsappUpdateForm({
            currentPassword: '',
            newWhatsApp: '',
            otp: new Array(6).fill(''),
            ref: ''
        });
        setAlerts(prev => ({
            ...prev,
            whatsapp: { show: false, type: '', message: '' }
        }));
    };

    const handleWhatsAppFormChange = (field, value) => {
        setWhatsappUpdateForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const showWhatsAppAlert = (severity, message) => {
        setAlerts(prev => ({
            ...prev,
            whatsapp: { show: true, type: severity, message }
        }));
    };

    const handleWhatsAppStepNext = async () => {
        if (whatsappUpdateStep === 1) {
            // Validate password
            if (!whatsappUpdateForm.currentPassword) {
                showWhatsAppAlert('error', 'Please enter your current password');
                return;
            }
            setWhatsappUpdateStep(2);
        } else if (whatsappUpdateStep === 2) {
            // Validate new WhatsApp and send OTP
            if (!whatsappUpdateForm.newWhatsApp) {
                showWhatsAppAlert('error', 'Please enter your new WhatsApp number');
                return;
            }

            const whatsappRegex = /^\+?[1-9]\d{7,14}$/;
            const cleanWhatsapp = whatsappUpdateForm.newWhatsApp.replace(/\s|-/g, '');
            if (!whatsappRegex.test(cleanWhatsapp)) {
                showWhatsAppAlert('error', 'Please provide a valid WhatsApp number');
                return;
            }

            const cleanDigits = cleanWhatsapp.replace(/\D/g, '');
            if (cleanDigits === profileForm.whatsapp.replace(/\D/g, '')) {
                showWhatsAppAlert('error', 'New WhatsApp number must be different from current number');
                return;
            }

            setWhatsappUpdateLoading(true);
            try {
                // Check if the number is already registered before sending OTP
                const checkResult = await ClientCheckWhatsappExists(cleanDigits);
                if (checkResult?.exists) {
                    showWhatsAppAlert('error', 'This WhatsApp number is already registered in the system. Please use a different number.');
                    setWhatsappUpdateLoading(false);
                    return;
                }

                const ref = generateUniqueId(10);
                const response = await ClientRequestWhatsAppOTP(cleanDigits, ref);

                if (response && (response.result === "success" || response.result === "partial_success")) {
                    setWhatsappUpdateForm(prev => ({
                        ...prev,
                        ref: ref,
                        newWhatsApp: cleanDigits
                    }));
                    setWhatsappUpdateStep(3);
                    showWhatsAppAlert('success', 'Verification code sent to your new WhatsApp number');
                } else {
                    showWhatsAppAlert('error', response?.message || 'Failed to send verification code');
                }
            } catch (error) {
                console.error('Error sending OTP:', error);
                showWhatsAppAlert('error', 'Failed to send verification code');
            } finally {
                setWhatsappUpdateLoading(false);
            }
        }
    };

    const handleWhatsAppStepBack = () => {
        if (whatsappUpdateStep > 1) {
            setWhatsappUpdateStep(whatsappUpdateStep - 1);
        }
    };

    const handleWhatsAppUpdate = async () => {
        if (!whatsappUpdateForm.otp.every(digit => digit !== '')) {
            showWhatsAppAlert('error', 'Please enter the complete verification code');
            return;
        }

        setWhatsappUpdateLoading(true);
        try {
            const otpString = whatsappUpdateForm.otp.join('');
            const response = await ClientUpdateWhatsAppNumber(
                whatsappUpdateForm.currentPassword,
                whatsappUpdateForm.newWhatsApp,
                otpString,
                whatsappUpdateForm.ref
            );

            if (response && response.success) {
                showWhatsAppAlert('success', 'WhatsApp number updated successfully!');
                closeWhatsAppDialog();
                setShowSuccessDialog(true);
                // Reload user data to get updated information
                loadUserData();
            } else {
                showWhatsAppAlert('error', response?.message || 'Failed to update WhatsApp number');
            }
        } catch (error) {
            console.error('Error updating WhatsApp number:', error);
            showWhatsAppAlert('error', 'An error occurred while updating your WhatsApp number');
        } finally {
            setWhatsappUpdateLoading(false);
        }
    };

    const handleCancelEdit = () => {
        // Reset form to original values using the same logic as loadUserData
        if (userAccount) {
            setProfileForm({
                name: userAccount.name || customer?.name || '',
                whatsapp: userAccount.whatsapp || customer?.whatsapp || '',
                default_route: userAccount.default_route || customer?.default_route || '',
                isDeliveryRequired: userAccount.isDeliveryRequired || customer?.isDeliveryRequired || false,
                deliveryAddresses: userAccount.deliveryAddresses || customer?.deliveryAddresses || [],
                isForwardRequired: userAccount.isForwardRequired || customer?.isForwardRequired || false,
                forwardAddresses: userAccount.forwardAddresses || customer?.forwardAddresses || []
            });
        } else if (customer) {
            setProfileForm({
                name: customer.name || '',
                whatsapp: customer.whatsapp || '',
                default_route: customer.default_route || '',
                isDeliveryRequired: customer.isDeliveryRequired || false,
                deliveryAddresses: customer.deliveryAddresses || [],
                isForwardRequired: customer.isForwardRequired || false,
                forwardAddresses: customer.forwardAddresses || []
            });
        } else {
            // Set empty form if no data available
            setProfileForm({
                name: '',
                whatsapp: '',
                default_route: '',
                isDeliveryRequired: false,
                deliveryAddresses: [],
                isForwardRequired: false,
                forwardAddresses: []
            });
        }
        setIsEditing(false);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 1200, p: 3 }}>
            <Typography variant="h4" gutterBottom color="secondary" sx={{ fontWeight: 600, mb: 3 }}>
                My Profile
            </Typography>

            {/* Profile Header - Always Visible */}
            <Card elevation={2} sx={{ borderRadius: 3, mb: 2 }}>
                <Box sx={{ p: 3 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'center', sm: 'center' },
                            gap: 2,
                        }}
                    >
                        <Box sx={{ 
                            position: 'relative', 
                            display: 'flex', 
                            justifyContent: 'center',
                            width: { xs: '100%', sm: 'auto' }
                        }}>
                            <Avatar
                                src={profilePicturePreview || (customer?.profilePicture ? `/uploads/profiles/${customer.profilePicture}` : null)}
                                sx={{
                                    width: { xs: '80vw', sm: 120, md: 140 },
                                    height: { xs: '80vw', sm: 120, md: 140 },
                                    maxWidth: { xs: '200px', sm: '120px', md: '140px' },
                                    maxHeight: { xs: '200px', sm: '120px', md: '140px' },
                                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                    flexShrink: 0,
                                }}
                            >
                                {profileForm.name ? profileForm.name.charAt(0).toUpperCase() : 'U'}
                            </Avatar>
                            
                            {/* Profile picture upload controls */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: -8,
                                    right: -8,
                                    display: 'flex',
                                    gap: 0.5
                                }}
                            >
                                {profilePicturePreview ? (
                                    <>
                                        <IconButton
                                            size="small"
                                            onClick={handleProfilePictureUpload}
                                            disabled={profilePictureUploading}
                                            sx={{
                                                backgroundColor: theme.palette.success.main,
                                                color: 'white',
                                                '&:hover': {
                                                    backgroundColor: theme.palette.success.dark,
                                                },
                                                width: 28,
                                                height: 28
                                            }}
                                        >
                                            {profilePictureUploading ? (
                                                <CircularProgress size={16} color="inherit" />
                                            ) : (
                                                <Save fontSize="small" />
                                            )}
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={cancelProfilePictureUpload}
                                            disabled={profilePictureUploading}
                                            sx={{
                                                backgroundColor: theme.palette.error.main,
                                                color: 'white',
                                                '&:hover': {
                                                    backgroundColor: theme.palette.error.dark,
                                                },
                                                width: 28,
                                                height: 28
                                            }}
                                        >
                                            <Cancel fontSize="small" />
                                        </IconButton>
                                    </>
                                ) : (
                                    <>
                                        <IconButton
                                            size="small"
                                            component="label"
                                            disabled={profilePictureUploading}
                                            sx={{
                                                backgroundColor: theme.palette.secondary.main,
                                                color: 'white',
                                                '&:hover': {
                                                    backgroundColor: theme.palette.secondary.dark,
                                                },
                                                width: 28,
                                                height: 28
                                            }}
                                        >
                                            <PhotoCamera fontSize="small" />
                                            <input
                                                type="file"
                                                hidden
                                                accept="image/*"
                                                ref={fileInputRef}
                                                onChange={handleFileSelect}
                                            />
                                        </IconButton>
                                        
                                        {(customer?.profilePicture || profilePicturePreview) && (
                                            <IconButton
                                                size="small"
                                                onClick={confirmDeleteProfilePicture}
                                                disabled={profilePictureUploading}
                                                sx={{
                                                    backgroundColor: theme.palette.error.main,
                                                    color: 'white',
                                                    '&:hover': {
                                                        backgroundColor: theme.palette.error.dark,
                                                    },
                                                    width: 28,
                                                    height: 28
                                                }}
                                            >
                                                {profilePictureUploading ? (
                                                    <CircularProgress size={16} color="inherit" />
                                                ) : (
                                                    <Delete fontSize="small" />
                                                )}
                                            </IconButton>
                                        )}
                                    </>
                                )}
                            </Box>
                        </Box>

                        <Box sx={{ flex: 1, width: '100%' }}>
                            <Typography variant="h4" gutterBottom sx={{ 
                                wordBreak: 'break-word',
                                textAlign: { xs: 'center', sm: 'left' }
                            }}>
                                {profileForm.name || 'User Name'} 
                            </Typography>

                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1}
                                alignItems={{ xs: 'center', sm: 'center' }}
                            >                                
                                <Chip
                                    icon={<Phone />}
                                    label={profileForm.whatsapp || 'No phone'}
                                    variant="outlined"
                                    size="small"
                                    sx={{ maxWidth: { xs: '100%', sm: 'auto' }, overflow: 'hidden', textOverflow: 'ellipsis' }}
                                />
                            </Stack>
                        </Box>

                        <Box sx={{ 
                            width: { xs: '100%', sm: 'auto' }, 
                            display: 'flex', 
                            flexDirection: 'column',
                            alignItems: { xs: 'flex-start', sm: 'flex-end' },
                            gap: 1
                        }}>
                            <Button
                                startIcon={<Lock />}
                                size='medium'
                                variant="outlined"
                                color='secondary'
                                onClick={openPasswordDialog}
                                fullWidth
                                sx={{
                                    minWidth: { xs: 'auto', sm: '220px' },
                                    width: { xs: '100%', sm: 'auto' },
                                    px: 1.5,
                                    // py: 1,
                                    borderRadius: 1.5,
                                    fontSize: '0.75rem',
                                    flexShrink: 0,
                                    height: 'fit-content',
                                    color: theme.palette.secondary.main,
                                    borderColor: theme.palette.secondary.main,
                                    '&:hover': {
                                        backgroundColor: theme.palette.secondary.main,
                                        color: theme.palette.secondary.contrastText,
                                    }
                                }}
                            >
                                Change Password
                            </Button>
                            <Button
                                startIcon={<Edit />}
                                variant="outlined"
                                size="medium"
                                onClick={openWhatsAppDialog}
                                fullWidth
                                sx={{
                                    minWidth: { xs: 'auto', sm: '220px' },
                                    width: { xs: '100%', sm: 'auto' },
                                    px: 1.5,
                                    // py: 1,
                                    borderRadius: 1.5,
                                    fontSize: '0.75rem',
                                    flexShrink: 0,
                                    height: 'fit-content',
                                    color: theme.palette.secondary.main,
                                    borderColor: theme.palette.secondary.main,
                                    '&:hover': {
                                        backgroundColor: theme.palette.secondary.main,
                                        color: theme.palette.secondary.contrastText,
                                    }
                                }}
                            >
                                Change WhatsApp Number
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Card>

            <Card elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                {/* Profile Information Section */}
                <Box sx={{ p: 3 }}>
                    {alerts.profile.show && (
                        <Alert
                            severity={alerts.profile.type}
                            sx={{ mb: 3 }}
                            onClose={() => setAlerts(prev => ({
                                ...prev,
                                profile: { show: false, type: '', message: '' }
                            }))}
                        >
                            {alerts.profile.message}
                        </Alert>
                    )}

                    <Grid container spacing={4}>
                        {/* Profile Form */}
                        <Grid item xs={12} md={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Person color="secondary" />
                                    Personal Information
                                </Typography>

                                {/* Edit Buttons in Top Right */}
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    {!isEditing ? (
                                        <Button
                                            size="small"
                                            startIcon={<Edit />}
                                            variant="contained"
                                            color="secondary"
                                            onClick={() => setIsEditing(true)}
                                            sx={{ borderRadius: 2 }}
                                        >
                                            Edit
                                        </Button>
                                    ) : (
                                        <Stack direction="row" spacing={1}>
                                            <Button
                                                size="small"
                                                startIcon={<Save />}
                                                variant="contained"
                                                color="secondary"
                                                onClick={handleUpdateProfile}
                                                disabled={updateLoading}
                                                sx={{ borderRadius: 2 }}
                                            >
                                                {updateLoading ? <CircularProgress size={16} /> : 'Save'}
                                            </Button>
                                            <Button
                                                size="small"
                                                startIcon={<Cancel />}
                                                variant="outlined"
                                                color="secondary"
                                                onClick={handleCancelEdit}
                                                sx={{ borderRadius: 2 }}
                                            >
                                                Cancel
                                            </Button>
                                        </Stack>
                                    )}
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>

                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Full Name *"
                                            value={profileForm.name}
                                            onChange={(e) => handleProfileInputChange('name', e.target.value)}
                                            disabled={!isEditing}
                                            variant="outlined"
                                            required
                                            error={isEditing && (!profileForm.name || !profileForm.name.trim())}
                                            helperText={isEditing && (!profileForm.name || !profileForm.name.trim()) ? 'Full name is required' : ''}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl
                                            fullWidth
                                            variant="outlined"
                                            disabled={!isEditing}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        >
                                            <InputLabel>Pickup Point</InputLabel>
                                            <Select
                                                value={profileForm.default_route}
                                                onChange={(e) => handleProfileInputChange('default_route', e.target.value)}
                                                label="Pickup Point"
                                            >
                                                <MenuItem value="">
                                                    <em>Select a pickup point</em>
                                                </MenuItem>
                                                {routes.map((route) => (
                                                    <MenuItem key={route.id} value={route.id}>
                                                        {route.name || route.id}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    {/* Delivery Section */}
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <Home color="secondary" />
                                            Home delivery (Vientiane Capital only)
                                        </Typography>
                                        <DeliveryAddressList
                                            deliveryAddresses={profileForm.deliveryAddresses}
                                            setDeliveryAddresses={setDeliveryAddresses}
                                            isDeliveryRequired={true}
                                        />
                                    </Grid>

                                    {/* Forward Section */}
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <LocalShipping color="secondary" />
                                            Send me with the local logistic company
                                        </Typography>
                                        <ForwardAddressList
                                            forwardAddresses={profileForm.forwardAddresses}
                                            setForwardAddresses={setForwardAddresses}
                                            isForwardRequired={true}
                                        />
                                    </Grid>


                                </Grid>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Card>

            {/* Change Password Dialog */}
            <Dialog
                open={showPasswordDialog}
                onClose={closePasswordDialog}
                maxWidth="sm"
                fullWidth
                key={showPasswordDialog ? 'password-dialog-open' : 'password-dialog-closed'}
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: 3
                    }
                }}
            >
                <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
                    <Lock color="secondary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h5" fontWeight={600}>
                        Change Password
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        Choose a strong password to keep your account secure
                    </Typography>
                </DialogTitle>

                <DialogContent sx={{ px: 3 }}>
                    {alerts.password.show && (
                        <Alert
                            severity={alerts.password.type}
                            sx={{ mb: 3 }}
                            onClose={() => setAlerts(prev => ({
                                ...prev,
                                password: { show: false, type: '', message: '' }
                            }))}
                        >
                            {alerts.password.message}
                        </Alert>
                    )}

                    <form autoComplete="off">
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    key="current-password-field"
                                    fullWidth
                                    label="Current Password"
                                    type={showPasswords.current ? 'text' : 'password'}
                                    value={passwordForm.currentPassword}
                                    onChange={(e) => handlePasswordInputChange('currentPassword', e.target.value)}
                                    variant="outlined"
                                    autoComplete="current-password"
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    InputProps={{
                                        endAdornment: (
                                            <IconButton onClick={() => togglePasswordVisibility('current')} edge="end">
                                                {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        )
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    key="new-password-field"
                                    fullWidth
                                    label="New Password"
                                    type={showPasswords.new ? 'text' : 'password'}
                                    value={passwordForm.newPassword}
                                    onChange={(e) => handlePasswordInputChange('newPassword', e.target.value)}
                                    variant="outlined"
                                    autoComplete="new-password"
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    InputProps={{
                                        endAdornment: (
                                            <IconButton onClick={() => togglePasswordVisibility('new')} edge="end">
                                                {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        )
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    key="confirm-password-field"
                                    fullWidth
                                    label="Confirm New Password"
                                    type={showPasswords.confirm ? 'text' : 'password'}
                                    value={passwordForm.confirmPassword}
                                    onChange={(e) => handlePasswordInputChange('confirmPassword', e.target.value)}
                                    variant="outlined"
                                    autoComplete="new-password"
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    InputProps={{
                                        endAdornment: (
                                            <IconButton onClick={() => togglePasswordVisibility('confirm')} edge="end">
                                                {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        )
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </form>

                    {/* Password Requirements */}
                    <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="subtitle2" gutterBottom>Password Requirements:</Typography>
                        <List dense>
                            <ListItem sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <CheckCircle color={passwordForm.newPassword.length >= 8 ? 'success' : 'disabled'} fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="At least 8 characters long"
                                    primaryTypographyProps={{ variant: 'body2' }}
                                />
                            </ListItem>
                            <ListItem sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <CheckCircle
                                        color={/[a-zA-Z]/.test(passwordForm.newPassword) && /\d/.test(passwordForm.newPassword) ? 'success' : 'disabled'}
                                        fontSize="small"
                                    />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Contains both letters and numbers"
                                    primaryTypographyProps={{ variant: 'body2' }}
                                />
                            </ListItem>
                            <ListItem sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <CheckCircle
                                        color={passwordForm.newPassword && passwordForm.confirmPassword && passwordForm.newPassword === passwordForm.confirmPassword ? 'success' : 'disabled'}
                                        fontSize="small"
                                    />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Passwords match"
                                    primaryTypographyProps={{ variant: 'body2' }}
                                />
                            </ListItem>
                            <ListItem sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <CheckCircle
                                        color={passwordForm.currentPassword ? 'success' : 'disabled'}
                                        fontSize="small"
                                    />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Current password provided"
                                    primaryTypographyProps={{ variant: 'body2' }}
                                />
                            </ListItem>
                        </List>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 3, gap: 1 }}>
                    <Button
                        onClick={closePasswordDialog}
                        variant="outlined"
                        color="secondary"
                        sx={{ borderRadius: 2, minWidth: 100 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleChangePassword}
                        disabled={updateLoading}
                        startIcon={updateLoading ? <CircularProgress size={20} /> : <Lock />}
                        sx={{
                            borderRadius: 2,
                            minWidth: 140,
                            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                        }}
                    >
                        {updateLoading ? 'Changing...' : 'Change Password'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* WhatsApp Update Dialog */}
            <Dialog
                open={showWhatsAppDialog}
                onClose={closeWhatsAppDialog}
                maxWidth="sm"
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: 3
                    }
                }}
            >
                <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
                    <Phone color="secondary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h5" fontWeight={600}>
                        Update WhatsApp Number
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        {whatsappUpdateStep === 1 && "Verify your identity with your current password"}
                        {whatsappUpdateStep === 2 && "Enter your new WhatsApp number"}
                        {whatsappUpdateStep === 3 && "Enter the verification code sent to your new number"}
                    </Typography>
                </DialogTitle>

                <DialogContent sx={{ px: 3 }}>
                    {alerts.whatsapp.show && (
                        <Alert
                            severity={alerts.whatsapp.type}
                            sx={{ mb: 3 }}
                            onClose={() => setAlerts(prev => ({
                                ...prev,
                                whatsapp: { show: false, type: '', message: '' }
                            }))}
                        >
                            {alerts.whatsapp.message}
                        </Alert>
                    )}

                    {/* Step 1: Password Verification */}
                    {whatsappUpdateStep === 1 && (
                        <Box>
                            <TextField
                                fullWidth
                                label="Current Password"
                                type="password"
                                value={whatsappUpdateForm.currentPassword}
                                onChange={(e) => handleWhatsAppFormChange('currentPassword', e.target.value)}
                                variant="outlined"
                                autoComplete="current-password"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                                We need to verify your identity before allowing WhatsApp number changes.
                            </Typography>
                        </Box>
                    )}

                    {/* Step 2: New WhatsApp Number */}
                    {whatsappUpdateStep === 2 && (
                        <Box>
                            <TextField
                                fullWidth
                                label="New WhatsApp Number"
                                value={whatsappUpdateForm.newWhatsApp}
                                onChange={(e) => handleWhatsAppFormChange('newWhatsApp', e.target.value)}
                                variant="outlined"
                                placeholder="e.g., +8562012345678"
                                helperText="Include country code for international numbers"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                                A verification code will be sent to this number via WhatsApp.
                            </Typography>
                        </Box>
                    )}

                    {/* Step 3: OTP Verification */}
                    {whatsappUpdateStep === 3 && (
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                                Enter the 6-digit code sent to your new WhatsApp number:
                            </Typography>
                            <Typography variant="h6" color="secondary" sx={{ mb: 3 }}>
                                +{whatsappUpdateForm.newWhatsApp}
                            </Typography>
                            <OTPInput
                                otp={whatsappUpdateForm.otp}
                                setOtp={(otp) => handleWhatsAppFormChange('otp', otp)}
                                length={6}
                                fontSize="16px"
                                boxWidth="45px"
                            />
                            {whatsappUpdateForm.ref && (
                                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                                    Reference: {whatsappUpdateForm.ref}
                                </Typography>
                            )}
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 3, gap: 1 }}>
                    <Button
                        onClick={whatsappUpdateStep === 1 ? closeWhatsAppDialog : handleWhatsAppStepBack}
                        variant="outlined"
                        color='secondary'
                        sx={{ borderRadius: 2, minWidth: 100 }}
                        disabled={whatsappUpdateLoading}
                    >
                        {whatsappUpdateStep === 1 ? 'Cancel' : 'Back'}
                    </Button>

                    {whatsappUpdateStep < 3 ? (
                        <Button
                            variant="contained"
                            color='secondary'
                            onClick={handleWhatsAppStepNext}
                            disabled={whatsappUpdateLoading}
                            startIcon={whatsappUpdateLoading ? <CircularProgress size={20} /> : <Send />}
                            // sx={{
                            //     borderRadius: 2,
                            //     minWidth: 140,
                            //     background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                            // }}
                        >
                            {whatsappUpdateLoading ? 'Processing...' :
                                whatsappUpdateStep === 1 ? 'Verify Password' : 'Send Code'}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            color='secondary'
                            onClick={handleWhatsAppUpdate}
                            disabled={whatsappUpdateLoading}
                            startIcon={whatsappUpdateLoading ? <CircularProgress size={20} /> : <CheckCircle />}
                            // sx={{
                            //     borderRadius: 2,
                            //     minWidth: 140,
                            //     background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                            // }}
                        >
                            {whatsappUpdateLoading ? 'Updating...' : 'Update Number'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Success Dialog */}
            <Dialog open={showSuccessDialog} onClose={() => setShowSuccessDialog(false)}>
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <CheckCircle color="success" sx={{ fontSize: 60, mb: 2 }} />
                    <Typography variant="h5">Success!</Typography>
                </DialogTitle>
                <DialogContent sx={{ textAlign: 'center', pb: 3 }}>
                    <Typography>
                        Your changes have been saved successfully.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                    <Button
                        onClick={() => setShowSuccessDialog(false)}
                        variant="contained"
                        color="secondary"
                        sx={{ borderRadius: 2, px: 4 }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Crop Dialog */}
            <Dialog
                open={cropDialogOpen}
                onClose={handleCancelCrop}
                maxWidth="sm"
                fullWidth
                sx={{ '& .MuiDialog-paper': { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography fontWeight={600} fontSize="1.1rem">Crop Profile Picture</Typography>
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                        Drag to reposition · Scroll or use the slider to zoom
                    </Typography>
                </DialogTitle>

                <DialogContent sx={{ p: 0, pb: '0 !important' }}>
                    {/* Crop area */}
                    <Box sx={{ position: 'relative', width: '100%', height: 360, background: '#111' }}>
                        {rawImageSrc && (
                            <Cropper
                                image={rawImageSrc}
                                crop={crop}
                                zoom={zoom}
                                aspect={1}
                                cropShape="round"
                                showGrid={false}
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
                            />
                        )}
                    </Box>

                    {/* Zoom slider */}
                    <Box sx={{ px: 3, pt: 2, pb: 1 }}>
                        <Typography variant="caption" color="text.secondary">Zoom</Typography>
                        <Slider
                            value={zoom}
                            min={1}
                            max={3}
                            step={0.05}
                            onChange={(_, v) => setZoom(v)}
                            color="secondary"
                            size="small"
                        />
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        onClick={handleCancelCrop}
                        variant="outlined"
                        color="secondary"
                        sx={{ borderRadius: 2, minWidth: 100 }}
                        disabled={cropApplying}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleApplyCrop}
                        variant="contained"
                        color="secondary"
                        disabled={cropApplying}
                        sx={{ borderRadius: 2, minWidth: 120 }}
                    >
                        {cropApplying ? <CircularProgress size={20} color="inherit" /> : 'Apply'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Profile Picture Delete Confirmation Dialog */}
            <Dialog
                open={showDeleteConfirmDialog} 
                onClose={() => setShowDeleteConfirmDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <Delete color="secondary" sx={{ fontSize: 60, mb: 2 }} />
                    <Typography variant="h5">Delete Profile Picture?</Typography>
                </DialogTitle>
                <DialogContent sx={{ textAlign: 'center', pb: 3 }}>
                    <Typography>
                        Are you sure you want to delete your profile picture? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
                    <Button
                        onClick={() => setShowDeleteConfirmDialog(false)}
                        variant="outlined"
                        color='secondary'
                        sx={{ borderRadius: 2, px: 3 }}
                        disabled={profilePictureUploading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleProfilePictureDelete}
                        variant="contained"
                        color="secondary"
                        sx={{ borderRadius: 2, px: 3 }}
                        disabled={profilePictureUploading}
                    >
                        {profilePictureUploading ? (
                            <>
                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                Deleting...
                            </>
                        ) : (
                            'Delete'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MyProfile;